import { Checkbox, TextInput } from '@any-ui-react/core';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { TeamAutocompleteField } from '@/common/components/employees/shared/TeamAutocompleteField';
import { BusinessUnitSelectSingle } from '@/common/components/shared/select/business-unit/BusinessUnitSelect';
import { DepartmentSelectSingle } from '@/common/components/shared/select/department/DepartmentSelect';
import { JobSelectSingle } from '@/common/components/shared/select/job/JobSelect';
import { JobLevelSelectSingle } from '@/common/components/shared/select/job-level/JobLevelSelect';
import { LocationSelectSingle } from '@/common/components/shared/select/location/LocationSelect';
import { JobHistoryLevel } from '@/common/graphql';
import { useContractTypePersistence } from '@/common/hooks/employees/useContractTypePersistence';
import { useGetGeneralDefinition } from '@/common/hooks/general/useGeneralDefinition';
import { type EmployeeAddFormDataType, FormAction } from '@/common/types';
import { isDepartmentTargetEligibleByDefault } from '@/common/utils';

interface Props {
  mode?: FormAction;
}

export const JobInformationFormFields = ({ mode = FormAction.CREATING }: Props) => {
  const {
    control,
    formState: { errors },
    register,
    setValue,
  } = useFormContext<EmployeeAddFormDataType>();

  const regionalRole = useWatch({ control, name: 'regionalRole' });
  const departmentLead = useWatch({ control, name: 'departmentLead' });
  const level = useWatch({ control, name: 'level' });
  const contractType = useWatch({ control, name: 'contractType' });
  const companyId = useWatch({ control, name: 'companyId' });
  const businessUnitId = useWatch({ control, name: 'businessUnitId' });
  const departmentId = useWatch({ control, name: 'departmentId' });
  const jobId = useWatch({ control, name: 'jobId' });

  const { data: allContractTypes } = useContractTypePersistence();
  const { businessUnitDefinition, departmentDefinition, jobDefinition } = useGetGeneralDefinition({
    type: 'Query',
    companyId,
    businessUnitId,
    departmentId,
    jobId,
  });

  return (
    <fieldset className="form-fieldset">
      <Controller
        control={control}
        name="level"
        render={({ field: { value } }) => (
          <div className="col-span-2 lg:col-span-1">
            <JobLevelSelectSingle
              error={errors.level?.message}
              label="Level"
              required={true}
              value={value}
              onChange={newValue => {
                const value = newValue?.value ?? null;
                setValue('level', value, { shouldDirty: true });
                setValue('departmentLead', value === JobHistoryLevel.L6);
              }}
            />
          </div>
        )}
      />

      <Controller
        control={control}
        name="locationId"
        render={({ field: { ...field } }) => (
          <div className="col-span-2 lg:col-span-1">
            <LocationSelectSingle
              {...field}
              error={errors.locationId?.message}
              label="Office Location"
              placeholder="Select Location"
              required={true}
              variables={{ enabled: mode === FormAction.CREATING || null }}
              onChange={option => {
                setValue('locationId', option?.value as string, {
                  shouldDirty: true,
                });
              }}
            />
          </div>
        )}
      />

      <div className="col-span-2">
        Business Unit and Department should be follow this rule:&nbsp;
        <a
          href="https://docs.google.com/spreadsheets/d/1Rtl1rAhD8JBgB2jtD3Znn5_3J5vmkYW_TAI8VC7oykI/preview"
          target="_blank"
          rel="noreferrer"
        >
          Company - Business Unit - Department relationships
        </a>
        .&nbsp;(depends on the company)
      </div>

      <Controller
        control={control}
        name="businessUnitId"
        render={({ field: { ...field } }) => {
          const disabled = !companyId;
          return (
            <div className="col-span-2 lg:col-span-1">
              <BusinessUnitSelectSingle
                {...field}
                disabled={disabled}
                error={errors.businessUnitId?.message}
                label="Business Unit"
                placeholder={disabled ? 'Select Company first' : 'Select Business Unit'}
                required={true}
                variables={{
                  enabled: mode === FormAction.CREATING ? true : undefined,
                  companyId: companyId || undefined,
                }}
                onChange={option => {
                  setValue('businessUnitId', option?.value as string, {
                    shouldDirty: true,
                  });
                  // Clear department when business unit changes
                  setValue('departmentId', null, {
                    shouldDirty: true,
                  });
                }}
              />
              {businessUnitDefinition && <div className="text-gray-7 ml-3 mt-1 text-2xs">{businessUnitDefinition}</div>}
            </div>
          );
        }}
      />
      <Controller
        control={control}
        name="departmentId"
        render={({ field: { ...field } }) => {
          const disabled = !businessUnitId;
          return (
            <div className="col-span-2 lg:col-span-1">
              <DepartmentSelectSingle
                {...field}
                disabled={disabled}
                error={errors.departmentId?.message}
                label="Department"
                placeholder={disabled ? 'Select Business Unit first' : 'Select Department'}
                required={true}
                variables={{
                  enabled: mode === FormAction.CREATING || null,
                  companyId: companyId || undefined,
                  businessUnitId: businessUnitId || undefined,
                }}
                onChange={option => {
                  setValue('departmentId', option?.value as string, {
                    shouldDirty: true,
                  });
                }}
              />
              {departmentDefinition && <div className="text-gray-7 ml-3 mt-1 text-2xs">{departmentDefinition}</div>}
            </div>
          );
        }}
      />

      <TeamAutocompleteField
        control={control}
        name="team"
        autoComplete="off"
        className="col-span-2 lg:col-span-1"
        data-1p-ignore={true}
        error={errors.team?.message}
        placeholder="Enter Team"
      />

      <Controller
        control={control}
        name="jobId"
        render={({ field: { ...field } }) => (
          <div className="col-span-2 lg:col-span-1">
            <JobSelectSingle
              {...field}
              error={errors.jobId?.message}
              label="Job"
              placeholder="Select Job Type"
              required={true}
              variables={{ enabled: mode === FormAction.CREATING || null }}
              {...field}
              onChange={option => {
                setValue('jobId', option?.value as string, {
                  shouldDirty: true,
                });
              }}
            />
            {jobDefinition && <div className="text-gray-7 ml-3 mt-1 text-2xs">{jobDefinition}</div>}
          </div>
        )}
      />

      <TextInput
        {...register('jobTitle')}
        autoComplete="off"
        className="col-span-2 lg:col-span-1"
        data-1p-ignore={true}
        disabled={allContractTypes.find(it => it.value === contractType)?.jobTitleShouldBeEmpty ?? false}
        error={errors.jobTitle?.message}
        label="Job Title"
        placeholder="Enter Job Title"
      />

      <div className="col-span-2">
        <Checkbox
          {...register('regionalRole')}
          checked={regionalRole}
          description="Turn on if this person's cost is allocated to another entity"
          error={errors.regionalRole?.message}
          label="Regional Role"
          onChange={e => {
            setValue('regionalRole', e.currentTarget.checked, {
              shouldDirty: true,
            });
          }}
        />
      </div>

      {isDepartmentTargetEligibleByDefault(level) && (
        <div className="col-span-2">
          <Checkbox
            {...register('departmentLead')}
            checked={departmentLead}
            description="Check if this employee is the Department Lead"
            label="Department Lead"
            onChange={e => {
              setValue('departmentLead', e.currentTarget.checked, {
                shouldDirty: true,
              });
            }}
          />
        </div>
      )}
    </fieldset>
  );
};
